import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

function mapDrive(row) { return snakeToCamel(row); }

// ── Tessie Status ────────────────────────────────────────────

export function useTessieStatus(options = {}) {
  return useQuery({
    queryKey: queryKeys.tessie.status,
    queryFn: () => api.get('/tessie/status'),
    staleTime: 60 * 1000,
    enabled: options.enabled !== false,
  });
}

// ── Tessie Config ────────────────────────────────────────────

export function useTessieConfig() {
  return useQuery({
    queryKey: queryKeys.tessie.config,
    queryFn: () => api.get('/tessie/config'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateTessieConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates) => api.put('/tessie/config', updates),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.config });
      logAction({
        action: 'Update Vehicle Settings',
        description: `Updated vehicle settings — ${data.vehicleName}, $${data.mileageRate}/mi, grace ${data.graceDays}d`,
        details: {
          vehicleName: data.vehicleName,
          mileageRate: data.mileageRate,
          graceDays: data.graceDays,
          stickyWindowMin: data.stickyWindowMin,
        },
      });
    },
  });
}

// ── Tesla account holders (from Tessie /drivers) ─────────────

export function useTeslaDrivers(options = {}) {
  return useQuery({
    queryKey: [...queryKeys.tessie.config, 'tesla-drivers'],
    queryFn: async () => {
      const data = await api.get('/tessie/tesla-drivers');
      return data.drivers || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled !== false,
  });
}

// ── Distinct driver_profile values seen on existing drives ───

export function useDriverProfilesSeen(options = {}) {
  return useQuery({
    queryKey: [...queryKeys.tessie.config, 'driver-profiles-seen'],
    queryFn: async () => {
      const data = await api.get('/tessie/driver-profiles-seen');
      return data.profiles || [];
    },
    staleTime: 60 * 1000,
    enabled: options.enabled !== false,
  });
}

// ── Re-map drivers on existing drives from driverMap ─────────

export function useRemapDrivers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/tessie/remap-drivers'),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.summary });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.status });
      logAction({
        action: 'Remap Drive Drivers',
        description: `Remapped ${data.updated} drives using ${data.mappings} driver mappings`,
        details: { updated: data.updated, mappings: data.mappings },
      });
    },
  });
}

// ── Drive GPS path (Tessie) ─────────────────────────────────

export function useDrivePath(driveId, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.tessie.drives, 'path', driveId],
    queryFn: async () => {
      const data = await api.get(`/tessie/drives/${driveId}/path`);
      return data.points || [];
    },
    enabled: !!driveId && enabled,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}

// ── Sync ─────────────────────────────────────────────────────

export function useTessieSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/tessie/sync'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.summary });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.config });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.status });
    },
  });
}

// ── Drives ───────────────────────────────────────────────────

export function useTessieDrives(filters = {}) {
  const { from, to, driver, tag, confirmed, limit = 100, offset = 0 } = filters;
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (driver) params.set('driver', driver);
  if (tag) params.set('tag', tag);
  if (confirmed !== undefined) params.set('confirmed', confirmed);
  params.set('limit', limit);
  params.set('offset', offset);

  const qs = params.toString();
  return useQuery({
    queryKey: [...queryKeys.tessie.drives, qs],
    queryFn: async () => {
      const data = await api.get(`/tessie/drives?${qs}`);
      return { ...data, drives: data.drives.map(mapDrive) };
    },
  });
}

// ── Drives grouped by local day ──────────────────────────────

export function useTessieDrivesByDay(filters = {}) {
  const { from, to, driver, confirmed } = filters;
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (driver) params.set('driver', driver);
  if (confirmed !== undefined) params.set('confirmed', confirmed);
  const qs = params.toString();
  return useQuery({
    queryKey: [...queryKeys.tessie.drives, 'by-day', qs],
    queryFn: async () => {
      const data = await api.get(`/tessie/drives/by-day?${qs}`);
      return {
        ...data,
        days: (data.days || []).map((d) => ({ ...d, drives: d.drives.map(mapDrive) })),
      };
    },
  });
}

// ── Resuggest drivers for unconfirmed drives ─────────────────

export function useResuggest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/tessie/drives/resuggest'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.status });
    },
  });
}

// ── Classify ─────────────────────────────────────────────────

export function useClassifyDrive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tag, rosterId, notes }) =>
      api.put(`/tessie/drives/${id}/classify`, { tag, rosterId, notes }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.summary });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.status });
      logAction({
        action: 'Classify Drive',
        description: `Classified drive ${variables.id} as ${variables.tag}`,
        details: {
          id: variables.id,
          tag: variables.tag,
          rosterId: variables.rosterId,
          notes: variables.notes,
        },
      });
    },
  });
}

export function useAssignDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rosterId }) =>
      api.put(`/tessie/drives/${id}/assign-driver`, { rosterId }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.summary });
      logAction({
        action: 'Assign Drive Driver',
        description: `Assigned drive ${variables.id} to driver ${variables.rosterId}`,
        details: { id: variables.id, rosterId: variables.rosterId },
      });
    },
  });
}

export function useBulkAssignDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driveIds, rosterId }) =>
      api.post('/tessie/drives/bulk-assign-driver', { driveIds, rosterId }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.summary });
      logAction({
        action: 'Bulk Assign Drive Driver',
        description: `Assigned ${variables.driveIds.length} drives to driver ${variables.rosterId}`,
        details: { driveCount: variables.driveIds.length, rosterId: variables.rosterId },
      });
    },
  });
}

export function useBulkClassifyDrives() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ driveIds, tag }) =>
      api.post('/tessie/drives/bulk-classify', { driveIds, tag }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.tessie.drives });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.summary });
      qc.invalidateQueries({ queryKey: queryKeys.tessie.status });
      logAction({
        action: 'Bulk Classify Drives',
        description: `Classified ${variables.driveIds.length} drives as ${variables.tag}`,
        details: { driveCount: variables.driveIds.length, tag: variables.tag },
      });
    },
  });
}

// ── Summary ──────────────────────────────────────────────────

export function useTessieSummary(filters = {}) {
  const { from, to } = filters;
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();

  return useQuery({
    queryKey: [...queryKeys.tessie.summary, qs],
    queryFn: () => api.get(`/tessie/summary?${qs}`),
  });
}
